import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ApiClient from "../../methods/api/apiClient";
import { isGuestMode, getGuestSessionId } from "../../methods/guestMode";
import { isApp } from "../../native/platform";
import { setExplainerActive } from "./explainerEvents";
import ExplainerTour from "./index";
import WelcomeModal from "./WelcomeModal";
import sidebarSteps from "./sidebarSteps";
import DASHBOARD_STEPS from "./dashboardSteps";

const SIDEBAR_KEY = "explainer_sidebar_done";
const SIDEBAR_WELCOME_KEY = "explainer_sidebar_welcome_seen";
const DASHBOARD_KEY = "explainer_dashboard_done";
const DASHBOARD_WELCOME_KEY = "explainer_dashboard_welcome_seen";

const lsGet = (key) => localStorage.getItem(key) === "true";
const lsSet = (key) => localStorage.setItem(key, "true");

const ExplainerManager = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const showProfileSelector = !(user?.accountType === "pro" && user?.role !== "hunter");
  const [showSidebarWelcome, setShowSidebarWelcome] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showDashboardWelcome, setShowDashboardWelcome] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [loading, setLoading] = useState(true);

  const pathname = location.pathname;
  const guest = isGuestMode();
  const onDashboard = pathname === "/dashboard";

  const explainerVisible =
    showSidebarWelcome ||
    showSidebar ||
    showDashboardWelcome ||
    showDashboard;

  useEffect(() => {
    setExplainerActive(explainerVisible);
  }, [explainerVisible]);

  useEffect(() => {
    const check = async () => {
      if (guest) {
        const sid = getGuestSessionId();
        const sideDone = lsGet(SIDEBAR_KEY) || lsGet(`${SIDEBAR_KEY}_${sid}`);
        const sideWelcomeSeen = lsGet(SIDEBAR_WELCOME_KEY) || lsGet(`${SIDEBAR_WELCOME_KEY}_${sid}`);
        const dashDone = lsGet(DASHBOARD_KEY);
        const dashWelcomeSeen = lsGet(DASHBOARD_WELCOME_KEY) || lsGet(`${DASHBOARD_WELCOME_KEY}_${sid}`);

        if (onDashboard) {
          if (!sideDone) {
            if (!sideWelcomeSeen) setShowSidebarWelcome(true);
            else setShowSidebar(true);
          } else if (!dashDone) {
            if (!dashWelcomeSeen) setShowDashboardWelcome(true);
            else setShowDashboard(true);
          }
        }
        setLoading(false);
      } else {
        try {
          const res = await ApiClient.get("/onboarding/state");
          if (res.success) {
            const sd = res.data;
            if (onDashboard) {
              if (!sd.explainerSidebarDone) setShowSidebar(true);
              else if (!sd.explainerDashboardDone) setShowDashboard(true);
            }
          }
        } catch (e) { /* ignore */ }
        setLoading(false);
      }
    };
    check();
  }, [pathname, guest, onDashboard]);

  const markWelcomeSeen = (key) => {
    const sid = getGuestSessionId();
    lsSet(key); lsSet(`${key}_${sid}`);
  };

  // Marque un type d'explainer comme terminé. `all` = « Ne plus afficher » :
  // l'utilisateur ne veut plus AUCUNE visite guidée (sidebar + dashboard).
  const markExplainerDone = async (type) => {
    if (guest) {
      const sid = getGuestSessionId();
      const keys = type === "all" ? [SIDEBAR_KEY, DASHBOARD_KEY] : [type === "sidebar" ? SIDEBAR_KEY : DASHBOARD_KEY];
      keys.forEach((k) => { lsSet(k); lsSet(`${k}_${sid}`); });
      return;
    }
    await ApiClient.post("/onboarding/explainer/done", { type }).catch(() => {});
  };

  const onSidebarWelcomeStart = () => {
    markWelcomeSeen(SIDEBAR_WELCOME_KEY);
    setShowSidebarWelcome(false); setShowSidebar(true);
  };
  // « Passer pour le moment » : on ferme la modale sans lancer la visite.
  const onSidebarWelcomeClose = () => {
    markWelcomeSeen(SIDEBAR_WELCOME_KEY);
    setShowSidebarWelcome(false);
  };
  const onSidebarFinish = async () => {
    await markExplainerDone("sidebar");
    setShowSidebar(false);
  };
  const onSidebarClose = async (dontShowAgain) => {
    // Invité : pas de case « Ne plus afficher » → quitter = visite faite.
    if (guest) await markExplainerDone("sidebar");
    else if (dontShowAgain) await markExplainerDone("all");
    setShowSidebar(false);
  };

  const onDashboardWelcomeStart = () => {
    markWelcomeSeen(DASHBOARD_WELCOME_KEY);
    setShowDashboardWelcome(false); setShowDashboard(true);
  };
  // « Plus tard » : on ferme la modale sans lancer la visite.
  const onDashboardWelcomeClose = () => {
    markWelcomeSeen(DASHBOARD_WELCOME_KEY);
    setShowDashboardWelcome(false);
  };
  const onDashboardFinish = async () => {
    await markExplainerDone("dashboard");
    setShowDashboard(false);
  };
  const onDashboardClose = async (dontShowAgain) => {
    if (guest) await markExplainerDone("dashboard");
    else if (dontShowAgain) await markExplainerDone("all");
    setShowDashboard(false);
  };

  if (loading) return null;

  // Explainers désactivés en app native et sur le web mobile (conservés sur le
  // web desktop uniquement).
  const isMobileWeb = typeof window !== "undefined" && window.innerWidth < 768;
  if (isApp() || isMobileWeb) return null;

  return (
    <>
      {showSidebarWelcome && (
        <WelcomeModal
          title={t("explainer.welcome.sidebar.title", "Bienvenue sur AnyHomes")}
          description={t("explainer.welcome.sidebar.desc", "Découvrez les fonctionnalités clés de la plateforme pour comprendre comment AnyHomes peut vous aider à concrétiser votre projet immobilier.")}
          cta={t("explainer.welcome.sidebar.cta", "Faire le tour des fonctionnalités")}
          skipLabel={t("explainer.welcome.sidebar.skip", "Passer pour le moment")}
          onStart={onSidebarWelcomeStart}
          onClose={onSidebarWelcomeClose}
        />
      )}
      {showSidebar && !showSidebarWelcome && (
        <ExplainerTour
          steps={sidebarSteps(t)}
          showCheckbox={!guest}
          storageKey={SIDEBAR_KEY}
          onFinish={onSidebarFinish}
          onClose={onSidebarClose}
        />
      )}
      {showDashboardWelcome && !showSidebar && (
        <WelcomeModal
          title={t("explainer.welcome.dashboard.title", "Votre tableau de bord")}
          description={t("explainer.welcome.dashboard.desc", "Le tableau de bord est l'écran central qui vous permet de piloter votre projet immobilier. Découvrez ses éléments clés.")}
          cta={t("explainer.welcome.dashboard.cta", "Découvrir le tableau de bord")}
          skipLabel={t("explainer.welcome.dashboard.skip", "Plus tard")}
          onStart={onDashboardWelcomeStart}
          onClose={onDashboardWelcomeClose}
        />
      )}
      {showDashboard && !showDashboardWelcome && (
        <ExplainerTour
          steps={DASHBOARD_STEPS(t, { showProfileSelector })}
          showCheckbox={!guest}
          storageKey={DASHBOARD_KEY}
          onFinish={onDashboardFinish}
          onClose={onDashboardClose}
        />
      )}
    </>
  );
};

export default ExplainerManager;
